// PageVitrineAsso.jsx — Phase G : Vitrine publique d'une asso (page de vente brandée)
// Accessible via : /asso/:slug OU directement par hostname custom domain
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Calendar, Euro, MapPin, ChevronRight, Facebook, Instagram, Linkedin, Twitter,
  Globe, Mail, Phone, Ticket, Sparkles, Check, X, ArrowRight, Building2, Heart,
} from 'lucide-react';

import { mediaUrl } from '@/lib/media';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PageVitrineAsso({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signupModal, setSignupModal] = useState(null); // formule
  const [showSuccess, setShowSuccess] = useState(searchParams.get('success') === 'formule');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    axios.get(`${API_BASE}/public/vitrine/${slug}`)
      .then(({ data }) => {
        setData(data);
        // Appliquer la couleur principale de l'asso au CSS variable
        if (data.asso?.couleur_principale) {
          document.documentElement.style.setProperty('--theme-primary', data.asso.couleur_principale);
        }
        document.title = `${data.asso.nom} · ${data.vitrine.accroche || 'Bienvenue'}`;
      })
      .catch(() => { /* affiche 404 plus bas */ })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!data) return (
    <div className="min-h-screen grid place-items-center text-center p-6" data-testid="vitrine-not-found">
      <div>
        <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-3" />
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Vitrine introuvable</h1>
        <p className="text-sm text-slate-500">Cette association n&apos;existe pas ou sa vitrine n&apos;est pas publiée.</p>
      </div>
    </div>
  );

  const { asso, vitrine, evenements, formules, sponsors } = data;
  const galerie = vitrine.galerie || [];
  const primary = asso.couleur_principale;
  const sections = vitrine.sections || {};

  return (
    <div className="min-h-screen bg-white" data-testid="vitrine-page">
      {showSuccess && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-emerald-500 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 fade-in" data-testid="vitrine-success-banner">
          <Check className="w-5 h-5" /> Inscription confirmée ! Consultez votre email.
          <button onClick={() => setShowSuccess(false)} className="ml-2 opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* NAV */}
      <nav className="border-b border-slate-100 sticky top-0 z-30 bg-white/95 backdrop-blur" data-testid="vitrine-nav">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {asso.logo_url ? (
              <img src={mediaUrl(asso.logo_url)} alt={asso.nom} className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-full grid place-items-center font-display font-black text-white text-sm" style={{ background: primary }}>{asso.nom.charAt(0)}</div>
            )}
            <div className="font-display font-black text-slate-900 truncate">{asso.nom}</div>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-sm">
            {sections.evenements && evenements.length > 0 && <a href="#evenements" className="px-3 py-1.5 hover:text-slate-900 text-slate-600">Événements</a>}
            {galerie.length > 0 && <a href="#galerie" className="px-3 py-1.5 hover:text-slate-900 text-slate-600">Galerie</a>}
            {sections.formules && formules.length > 0 && <a href="#formules" className="px-3 py-1.5 hover:text-slate-900 text-slate-600">Adhésion</a>}
            {sections.contact && <a href="#contact" className="px-3 py-1.5 hover:text-slate-900 text-slate-600">Contact</a>}
            <a href="/login" className="ml-2 px-4 py-1.5 rounded-full text-white text-sm font-bold" style={{ background: primary }} data-testid="vitrine-login-btn">Espace membre →</a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      {sections.hero && (
        <section className="relative overflow-hidden" data-testid="vitrine-hero">
          {vitrine.photo_couverture && (
            <div className="absolute inset-0 -z-10">
              <img src={mediaUrl(vitrine.photo_couverture)} alt="" className="w-full h-full object-cover opacity-20" />
              <div className="absolute inset-0 bg-gradient-to-b from-white via-white/80 to-white" />
            </div>
          )}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
            <div className="inline-block mb-4 px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase" style={{ background: primary + '20', color: primary }}>
              {asso.type || 'Association'} {asso.ville && `· ${asso.ville}`}
            </div>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black text-slate-900 mb-4 leading-[1.05]" data-testid="vitrine-accroche">
              {vitrine.accroche}
            </h1>
            {vitrine.sous_titre && (
              <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-8" data-testid="vitrine-sous-titre">{vitrine.sous_titre}</p>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              {sections.evenements && evenements.length > 0 && (
                <a href="#evenements" className="px-6 py-3 rounded-full text-white font-bold text-base inline-flex items-center gap-2 hover:opacity-90 transition-opacity" style={{ background: primary }} data-testid="vitrine-cta-primary">
                  {vitrine.cta_principal} <ArrowRight className="w-4 h-4" />
                </a>
              )}
              {sections.formules && formules.length > 0 && (
                <a href="#formules" className="px-6 py-3 rounded-full border-2 border-slate-200 text-slate-900 font-bold text-base inline-flex items-center gap-2 hover:border-slate-400 transition-colors" data-testid="vitrine-cta-secondary">
                  {vitrine.cta_secondaire} <Heart className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* DESCRIPTION LONGUE */}
      {vitrine.description_longue && (
        <section className="py-12 sm:py-16 bg-slate-50" data-testid="vitrine-description">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-line">{vitrine.description_longue}</p>
          </div>
        </section>
      )}

      {/* ÉVÉNEMENTS */}
      {sections.evenements && evenements.length > 0 && (
        <section id="evenements" className="py-16 sm:py-20" data-testid="vitrine-evenements">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="mb-10 text-center">
              <div className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ background: primary + '20', color: primary }}>À l&apos;affiche</div>
              <h2 className="font-display text-3xl sm:text-5xl font-black text-slate-900">Prochains événements</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vitrine-events-grid">
              {evenements.map((e) => (
                <EventCard key={e.id} evt={e} primary={primary} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALERIE PHOTOS */}
      {galerie.length > 0 && (
        <section id="galerie" className="py-16 sm:py-20 bg-slate-50" data-testid="vitrine-galerie">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="mb-10 text-center">
              <div className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ background: primary + '20', color: primary }}>En images</div>
              <h2 className="font-display text-3xl sm:text-5xl font-black text-slate-900">Galerie</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="vitrine-galerie-grid">
              {galerie.map((g, i) => (
                <button
                  key={i} type="button" onClick={() => setLightbox(i)}
                  className="group relative aspect-square overflow-hidden rounded-2xl bg-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{ '--tw-ring-color': primary }}
                  data-testid={`vitrine-galerie-item-${i}`}
                >
                  <img src={mediaUrl(g.url)} alt={g.legende || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  {g.legende && (
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-white text-xs text-left opacity-0 group-hover:opacity-100 transition-opacity">
                      {g.legende}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LIGHTBOX GALERIE */}
      {lightbox !== null && galerie[lightbox] && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 grid place-items-center p-4 fade-in"
          onClick={() => setLightbox(null)}
          data-testid="vitrine-galerie-lightbox"
        >
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" data-testid="galerie-lightbox-close"><X className="w-5 h-5" /></button>
          {lightbox > 0 && (
            <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }} className="absolute left-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" data-testid="galerie-lightbox-prev"><ChevronRight className="w-5 h-5 rotate-180" /></button>
          )}
          {lightbox < galerie.length - 1 && (
            <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }} className="absolute right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" data-testid="galerie-lightbox-next"><ChevronRight className="w-5 h-5" /></button>
          )}
          <figure className="max-w-4xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img src={mediaUrl(galerie[lightbox].url)} alt={galerie[lightbox].legende || ''} className="max-w-full max-h-[78vh] object-contain rounded-lg mx-auto" />
            {galerie[lightbox].legende && <figcaption className="text-center text-white/80 text-sm mt-3">{galerie[lightbox].legende}</figcaption>}
          </figure>
        </div>
      )}

      {/* FORMULES D'ADHÉSION */}
      {sections.formules && formules.length > 0 && (
        <section id="formules" className="py-16 sm:py-20 bg-slate-50" data-testid="vitrine-formules">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="mb-10 text-center">
              <div className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ background: primary + '20', color: primary }}>Rejoignez-nous</div>
              <h2 className="font-display text-3xl sm:text-5xl font-black text-slate-900">Formules d&apos;adhésion</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto" data-testid="vitrine-formules-grid">
              {formules.map((f, idx) => (
                <FormuleCard key={f.id} f={f} primary={primary} featured={idx === Math.floor(formules.length / 2)} onChoose={() => setSignupModal(f)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPONSORS */}
      {sections.sponsors && sponsors.length > 0 && (
        <section className="py-12 sm:py-16" data-testid="vitrine-sponsors">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-6">Ils nous soutiennent</div>
            <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-10" data-testid="vitrine-sponsors-list">
              {sponsors.map((s) => (
                <a key={s.id} href={s.site_web || '#'} target="_blank" rel="noreferrer" className="opacity-60 hover:opacity-100 transition-opacity">
                  {s.logo_url ? (
                    <img src={mediaUrl(s.logo_url)} alt={s.nom} className="h-10 object-contain max-w-[160px]" />
                  ) : (
                    <span className="font-display font-bold text-slate-700 text-sm">{s.nom}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT */}
      {sections.contact && (
        <section id="contact" className="py-16 sm:py-20 bg-slate-900 text-white" data-testid="vitrine-contact">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ background: primary + '30', color: primary }}>Contact</div>
            <h2 className="font-display text-3xl sm:text-5xl font-black mb-8">Une question ? Écrivez-nous.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-10">
              {vitrine.contact?.email && (
                <a href={`mailto:${vitrine.contact.email}`} className="flex flex-col items-center gap-2 p-5 bg-white/5 backdrop-blur rounded-2xl border border-white/10 hover:bg-white/10 transition-colors" data-testid="vitrine-contact-email">
                  <Mail className="w-5 h-5" style={{ color: primary }} />
                  <span className="text-xs uppercase tracking-wider text-white/40">Email</span>
                  <span className="font-medium text-sm">{vitrine.contact.email}</span>
                </a>
              )}
              {vitrine.contact?.telephone && (
                <a href={`tel:${vitrine.contact.telephone.replace(/\s/g, '')}`} className="flex flex-col items-center gap-2 p-5 bg-white/5 backdrop-blur rounded-2xl border border-white/10 hover:bg-white/10 transition-colors" data-testid="vitrine-contact-phone">
                  <Phone className="w-5 h-5" style={{ color: primary }} />
                  <span className="text-xs uppercase tracking-wider text-white/40">Téléphone</span>
                  <span className="font-medium text-sm">{vitrine.contact.telephone}</span>
                </a>
              )}
              {vitrine.contact?.adresse && (
                <div className="flex flex-col items-center gap-2 p-5 bg-white/5 backdrop-blur rounded-2xl border border-white/10">
                  <MapPin className="w-5 h-5" style={{ color: primary }} />
                  <span className="text-xs uppercase tracking-wider text-white/40">Adresse</span>
                  <span className="font-medium text-sm text-center">{vitrine.contact.adresse}</span>
                </div>
              )}
            </div>
            {/* Réseaux sociaux */}
            <SocialLinks liens={vitrine.liens_sociaux} primary={primary} />
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="py-8 bg-slate-950 text-slate-500 text-center text-xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          © {new Date().getFullYear()} {asso.nom} · Propulsé par <a href="https://assoperf.fr" className="text-white hover:opacity-80">AssoPerf</a>
        </div>
      </footer>

      {/* Modal inscription formule */}
      {signupModal && <InscriptionFormuleModal slug={slug} formule={signupModal} primary={primary} onClose={() => setSignupModal(null)} />}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function EventCard({ evt, primary }) {
  const date = new Date(evt.date_debut);
  return (
    <a href={`/billetterie?event_id=${evt.id}`} target="_blank" rel="noreferrer" className="block group border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all hover:-translate-y-0.5" data-testid={`vitrine-evt-${evt.id}`}>
      {evt.image_url ? (
        <div className="aspect-[16/9] overflow-hidden">
          <img src={mediaUrl(evt.image_url)} alt={evt.nom} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      ) : (
        <div className="aspect-[16/9] grid place-items-center text-white text-center p-6" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)` }}>
          <div>
            <div className="font-display text-3xl font-black">{date.toLocaleDateString('fr-FR', { day: 'numeric' })}</div>
            <div className="text-xs uppercase tracking-wider opacity-80">{date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })}</div>
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{evt.type || 'Événement'}</div>
        <h3 className="font-display text-lg font-black text-slate-900 mb-2 line-clamp-2 group-hover:underline">{evt.nom}</h3>
        {evt.lieu && <p className="text-xs text-slate-500 flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" /> {evt.lieu}</p>}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <span className="text-sm font-bold" style={{ color: primary }}>
            {evt.prix_max > 0
              ? evt.prix_min === evt.prix_max ? `${evt.prix_min}€` : `dès ${evt.prix_min}€`
              : 'Gratuit'}
          </span>
          <span className="text-xs font-bold text-slate-500 flex items-center gap-0.5">Réserver <ChevronRight className="w-3 h-3" /></span>
        </div>
      </div>
    </a>
  );
}

function FormuleCard({ f, primary, featured, onChoose }) {
  const prix = (f.prix_cts || 0) / 100;
  const periodLabel = f.periodicite || f.periode_label || (f.periode && f.periode !== 'unique' ? f.periode : '');
  return (
    <div className={`relative card !p-6 text-center ${featured ? 'shadow-xl ring-2' : ''}`} style={featured ? { ringColor: primary, borderColor: primary } : {}} data-testid={`vitrine-formule-${f.id}`}>
      {featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: primary }}>Recommandée</div>}
      <h3 className="font-display text-xl font-black text-slate-900 mb-1">{f.nom}</h3>
      <div className="font-display text-4xl font-black my-3" style={{ color: featured ? primary : '#0F172A' }}>
        {prix === 0 ? 'Gratuit' : `${prix}€`}
        {periodLabel && prix > 0 && <span className="text-xs font-normal text-slate-500"> / {periodLabel}</span>}
      </div>
      {f.description && <p className="text-sm text-slate-600 mb-4 min-h-[3em]">{f.description}</p>}
      <button onClick={onChoose} className="w-full py-2.5 rounded-full font-bold text-sm transition-all hover:opacity-90" style={{ background: featured ? primary : '#0F172A', color: 'white' }} data-testid={`vitrine-choose-${f.id}`}>
        Choisir <ArrowRight className="inline w-3.5 h-3.5 ml-1" />
      </button>
    </div>
  );
}

function SocialLinks({ liens, primary }) {
  const items = [
    { key: 'facebook', icon: Facebook, url: liens?.facebook },
    { key: 'instagram', icon: Instagram, url: liens?.instagram },
    { key: 'linkedin', icon: Linkedin, url: liens?.linkedin },
    { key: 'twitter', icon: Twitter, url: liens?.twitter },
    { key: 'site_web', icon: Globe, url: liens?.site_web },
  ].filter((x) => x.url);
  if (items.length === 0) return null;
  return (
    <div className="flex justify-center gap-3" data-testid="vitrine-social-links">
      {items.map(({ key, icon: Icon, url }) => (
        <a key={key} href={url.startsWith('http') ? url : `https://${url}`} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full grid place-items-center bg-white/10 hover:bg-white/20 transition-colors" data-testid={`vitrine-social-${key}`}>
          <Icon className="w-4 h-4" style={{ color: primary }} />
        </a>
      ))}
    </div>
  );
}

function InscriptionFormuleModal({ slug, formule, primary, onClose }) {
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', telephone: '' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Email requis'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/vitrine/${slug}/inscription-formule`, {
        ...form, formule_id: formule.id,
      });
      if (data.statut === 'inscrit_gratuit') {
        toast.success('Inscription confirmée ! Vérifiez votre email.');
        onClose();
        window.location.search = '?success=formule';
      } else if (data.checkout_url) {
        toast.success('Redirection vers le paiement…');
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur lors de l\'inscription');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in" onClick={onClose} data-testid="vitrine-signup-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: primary }}>Inscription</div>
            <h3 className="font-display text-xl font-black text-slate-900">{formule.nom}</h3>
            <div className="text-sm text-slate-500 mt-0.5">{formule.prix_cts === 0 ? 'Gratuit' : `${formule.prix_cts / 100}€${formule.periodicite ? ' / ' + formule.periodicite : ''}`}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Fermer"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} placeholder="Prénom *" className="input-base" required data-testid="vitrine-signup-prenom" />
            <input type="text" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Nom" className="input-base" data-testid="vitrine-signup-nom" />
          </div>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="input-base" required data-testid="vitrine-signup-email" />
          <input type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="Téléphone (optionnel)" className="input-base" data-testid="vitrine-signup-tel" />
          <p className="text-xs text-slate-500">
            {formule.prix_cts === 0 ? 'Vous recevrez un email de confirmation avec votre accès à l\'espace membre.' : 'Vous serez redirigé vers la page de paiement sécurisé Stripe.'}
          </p>
        </div>
        <button type="submit" disabled={saving} className="w-full mt-5 py-3 rounded-full font-bold text-white inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50" style={{ background: primary }} data-testid="vitrine-signup-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : formule.prix_cts === 0 ? <><Check className="w-4 h-4" /> Confirmer l&apos;inscription</> : <><Euro className="w-4 h-4" /> Payer {formule.prix_cts / 100}€</>}
        </button>
      </form>
    </div>
  );
}
